import { apiClient } from './apiClient';
import { camelize } from './_caseTransform';
import type { Plan } from '../types/plan';
import { STORAGE_KEYS } from '../utils/seedMockData';

// Get mock plans from localStorage (seeded at app startup)
function getMockPlans(): Plan[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.plans);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// The API serializes `price_per_month_usd` as a Decimal → JSON string ("0.00").
// Coerce it to a number at the boundary so the rest of the app can treat Plan
// fields as the numeric types the TS model promises.
function normalizePlan(plan: Plan): Plan {
  if (!plan) return plan;
  return {
    ...plan,
    pricePerMonthUsd:
      plan.pricePerMonthUsd != null ? Number(plan.pricePerMonthUsd) : 0,
    // Decimals too — serialized as strings.
    includedComputeUnitsPerMonth:
      plan.includedComputeUnitsPerMonth != null ? Number(plan.includedComputeUnitsPerMonth) : null,
    spendThresholdUsd: plan.spendThresholdUsd != null ? Number(plan.spendThresholdUsd) : null,
  };
}

export const planService = {
  /**
   * Get all available plans
   */
  async getPlans(): Promise<Plan[]> {
    // Inactive plans too: an organization can sit on a plan that is no longer sold
    // (Free on the public cloud) and its page must still name it. Pickers filter isActive.
    const response = camelize(await apiClient.getAll<Plan>(
      '/plans/?active_only=false',
      getMockPlans()
    ));
    return (response.data || []).map(normalizePlan);
  },

  /**
   * Get a single plan by ID
   */
  async getPlanById(id: string): Promise<Plan | null> {
    const mockPlan = getMockPlans().find(p => p.id === id);

    const response = camelize(await apiClient.get<Plan>(
      `/plans/${id}`,
      mockPlan || undefined
    ));
    return response.data ? normalizePlan(response.data) : null;
  },

  /**
   * Get a plan by name (e.g., 'free', 'standard', 'enterprise')
   */
  async getPlanByName(name: string): Promise<Plan | null> {
    const mockPlan = getMockPlans().find(p => p.name === name);

    const response = camelize(await apiClient.get<Plan>(
      `/plans/by-name/${name}`,
      mockPlan || undefined
    ));
    return response.data ? normalizePlan(response.data) : null;
  },

  /**
   * Get active plans only (for display in upgrade UI)
   */
  async getActivePlans(): Promise<Plan[]> {
    const allPlans = await this.getPlans();
    return allPlans.filter(p => p.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  },
};
